<template>
  <!-- Main Container with consistent dashboard styling -->
  <v-container class="d-flex flex-column" fluid>
    <!-- Hidden TypographySettings to preload fonts -->
    <div style="display: none">
      <TypographySettings
        v-model="hiddenTypography"
        :excluded="[
          'family',
          'textAlignment',
          'style',
          'decoration',
          'letterSpacing',
          'transform',
        ]"
        @fonts-loaded="handleFontsLoaded"
      />
    </div>

    <!-- Header Section - matching dashboard style -->
    <v-card class="mb-6" elevation="2">
      <v-card-text class="pa-6">
        <div
          class="d-flex flex-column flex-md-row align-center justify-space-between mb-4"
        >
          <div>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Customize QR code layout and appearance for printing
            </p>
          </div>
          <div class="text-center text-md-right">
            <div class="text-h6 font-weight-medium text-primary">
              {{ hasUnsavedChanges ? 'Unsaved Changes' : 'Preview Active' }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{
                hasUnsavedChanges
                  ? 'Save to apply changes'
                  : 'Real-time updates'
              }}
            </div>
          </div>
        </div>

        <!-- Action Buttons - matching dashboard style -->
        <div class="d-flex flex-column flex-md-row gap-4 align-center">
          <v-btn
            @click="handleSave"
            :loading="productSettings.loading"
            :disabled="!hasUnsavedChanges"
            color="primary"
            size="large"
            prepend-icon="mdi-content-save"
            class="px-8"
            data-testid="save-settings-btn"
          >
            {{ hasUnsavedChanges ? 'Save Settings' : 'No Changes' }}
          </v-btn>

          <v-btn
            v-if="hasUnsavedChanges"
            @click="handleReset"
            variant="outlined"
            color="secondary"
            prepend-icon="mdi-refresh"
            data-testid="reset-to-default-btn"
          >
            Reset to Default
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Content Section - matching dashboard layout -->
    <div class="d-flex ga-10 flex-column flex-md-row">
      <!-- Left Panel: Preview - using consistent card styling -->
      <div class="flex-shrink-0" style="width: 450px; max-width: 100%">
        <PrintPreview
          :above-preview-img="productSettings.aboveImageSettings.file"
          :above-preview-text="productSettings.aboveTextSettings.text"
          :below-preview-img="productSettings.belowImageSettings.file"
          :below-preview-text="productSettings.belowTextSettings.text"
        />
      </div>

      <!-- Right Panel: Settings - using consistent card styling -->
      <div class="flex-grow-1">
        <v-card elevation="2">
          <v-card-text class="pa-6">
            <!-- Settings Tabs - improved styling -->
            <v-tabs v-model="activeSettingsTab" class="mb-6">
              <v-tab
                value="customText"
                class="text-body-1"
                data-testid="custom-text-tab"
              >
                <v-icon start class="mr-2">mdi-text</v-icon>
                <span class="d-none d-lg-block">Custom Text</span>
              </v-tab>
              <v-tab
                value="vehicleTitle"
                class="text-body-1"
                data-testid="vehicle-title-tab"
              >
                <v-icon start class="mr-2">mdi-car</v-icon>
                <span class="d-none d-lg-block">Vehicle Title</span>
              </v-tab>
              <v-tab
                value="typography"
                class="text-body-1"
                data-testid="typography-tab"
              >
                <v-icon start class="mr-2">mdi-format-font</v-icon>
                <span class="d-none d-lg-block">Typography</span>
              </v-tab>
            </v-tabs>

            <!-- Tab Content with consistent styling -->
            <v-tabs-window v-model="activeSettingsTab">
              <v-tabs-window-item value="customText">
                <div class="space-y-6">
                  <!-- Above QR Image Settings -->
                  <v-expansion-panels variant="accordion">
                    <v-expansion-panel
                      title="Above QR Code Image Settings"
                      class="mb-4"
                      data-testid="above-qr-image-panel"
                    >
                      <v-expansion-panel-text>
                        <div class="space-y-4">
                          <v-checkbox
                            hide-details
                            label="Show image"
                            v-model="productSettings.showBrandImage"
                            color="primary"
                            @update:modelValue="
                              (v: boolean | null) => onToggleShowImage(!!v)
                            "
                          />

                          <v-checkbox
                            hide-details
                            label="Use Custom Image"
                            v-model="productSettings.useCustomImageAbove"
                            color="primary"
                            data-testid="use-custom-image-toggle"
                            @update:modelValue="
                              (v: boolean | null) => onToggleUseCustomImage(!!v)
                            "
                          />

                          <v-file-input
                            v-if="productSettings.useCustomImageAbove"
                            accept="image/*"
                            label="Upload custom image"
                            variant="outlined"
                            prepend-inner-icon="mdi-image"
                            @update:modelValue="onCustomImageSelected"
                            data-testid="custom-image-upload"
                          />

                          <div
                            v-if="
                              productSettings.showBrandImage ||
                              productSettings.useCustomImageAbove
                            "
                            class="space-y-4"
                          >
                            <v-text-field
                              label="Width (px)"
                              v-model="productSettings.aboveImageSettings.width"
                              variant="outlined"
                              type="number"
                              prepend-inner-icon="mdi-arrow-expand-horizontal"
                            />
                            <v-select
                              label="Alignment"
                              v-model="productSettings.aboveImageSettings.align"
                              :items="[
                                { title: 'Left', value: 'left' },
                                { title: 'Center', value: 'center' },
                                { title: 'Right', value: 'right' },
                              ]"
                              variant="outlined"
                              :prepend-inner-icon="
                                getAlignmentIcon(
                                  productSettings.aboveImageSettings.align,
                                )
                              "
                            />
                          </div>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <!-- Above QR Text Settings -->
                  <v-expansion-panels variant="accordion">
                    <v-expansion-panel
                      title="Above QR Code"
                      class="mb-4"
                      data-testid="above-qr-text-panel"
                    >
                      <v-expansion-panel-text>
                        <div class="space-y-4">
                          <v-textarea
                            label="Text content"
                            v-model="productSettings.aboveTextSettings.text"
                            rows="3"
                            auto-grow
                            variant="outlined"
                            data-testid="above-qr-text-input"
                          />
                          <v-select
                            label="Text alignment"
                            v-model="productSettings.aboveTextSettings.align"
                            :items="[
                              { title: 'Left', value: 'left' },
                              { title: 'Center', value: 'center' },
                              { title: 'Right', value: 'right' },
                            ]"
                            variant="outlined"
                            :prepend-inner-icon="
                              getAlignmentIcon(
                                productSettings.aboveTextSettings.align,
                              )
                            "
                            data-testid="above-qr-alignment-select"
                          />
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <!-- Below QR Text Settings -->
                  <v-expansion-panels variant="accordion">
                    <v-expansion-panel
                      title="Below QR Code"
                      data-testid="below-qr-text-panel"
                    >
                      <v-expansion-panel-text>
                        <div class="space-y-4">
                          <v-textarea
                            label="Text content"
                            v-model="productSettings.belowTextSettings.text"
                            rows="3"
                            auto-grow
                            variant="outlined"
                            data-testid="below-qr-text-input"
                          />
                          <v-select
                            label="Text alignment"
                            v-model="productSettings.belowTextSettings.align"
                            :items="[
                              { title: 'Left', value: 'left' },
                              { title: 'Center', value: 'center' },
                              { title: 'Right', value: 'right' },
                            ]"
                            variant="outlined"
                            :prepend-inner-icon="
                              getAlignmentIcon(
                                productSettings.belowTextSettings.align,
                              )
                            "
                            data-testid="below-qr-alignment-select"
                          />
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </v-tabs-window-item>

              <v-tabs-window-item value="vehicleTitle">
                <VehicleTitle />
              </v-tabs-window-item>

              <v-tabs-window-item value="typography">
                <TypographySettings />
              </v-tabs-window-item>
            </v-tabs-window>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import VehicleTitle from './VehicleTitle.vue'
import TypographySettings from './TypographySettings.vue'
import PrintPreview from './PrintPreview.vue'
import { useAccountSettingsStore } from '@/store/accountSettings'
import { useProductSettingsStore } from '@/store/productSettings'
import { getPresignedUploadUrl, putFileToS3, inferExtension } from '@/upload'
import { getComponentsBucketName } from '@/utils'

const productSettings = useProductSettingsStore()
const accountSettings = useAccountSettingsStore()
const activeSettingsTab = ref('customText')

// Store original settings for comparison
const originalSettings = ref<any>(null)

// Hidden typography data for preloading fonts
const hiddenTypography = ref({
  fontSize: '16',
  fontSizeUnit: 'px',
  lineHeight: '1.5',
  lineHeightUnit: 'em',
  fontWeight: 'normal',
})

// Capture original settings on mount
onMounted(() => {
  captureOriginalSettings()
})

// Capture original settings for comparison
const captureOriginalSettings = () => {
  originalSettings.value = {
    aboveTextSettings: { ...productSettings.aboveTextSettings },
    belowTextSettings: { ...productSettings.belowTextSettings },
    // store image settings without transient File objects
    aboveImageSettings: (() => {
      const { file, ...rest } = productSettings.aboveImageSettings as any
      return { ...rest }
    })(),
    belowImageSettings: (() => {
      const { file, ...rest } = productSettings.belowImageSettings as any
      return { ...rest }
    })(),
    typographySettings: { ...productSettings.typographySettings },
    showBrandImage: productSettings.showBrandImage,
    brandImage: productSettings.brandImage,
    vehicleTitleSettings: { ...productSettings.vehicleTitleSettings },
    useCustomImageAbove: productSettings.useCustomImageAbove,
    imageUrl: productSettings.imageUrl,
  }
}

// Check if there are unsaved changes
const hasUnsavedChanges = computed(() => {
  if (!originalSettings.value) return false

  const sanitizeImage = (img: any) => {
    const { file, ...rest } = img || {}
    return rest
  }

  return (
    JSON.stringify(productSettings.aboveTextSettings) !==
      JSON.stringify(originalSettings.value.aboveTextSettings) ||
    JSON.stringify(productSettings.belowTextSettings) !==
      JSON.stringify(originalSettings.value.belowTextSettings) ||
    JSON.stringify(sanitizeImage(productSettings.aboveImageSettings)) !==
      JSON.stringify(originalSettings.value.aboveImageSettings) ||
    JSON.stringify(sanitizeImage(productSettings.belowImageSettings)) !==
      JSON.stringify(originalSettings.value.belowImageSettings) ||
    JSON.stringify(productSettings.typographySettings) !==
      JSON.stringify(originalSettings.value.typographySettings) ||
    productSettings.showBrandImage !== originalSettings.value.showBrandImage ||
    productSettings.brandImage !== originalSettings.value.brandImage ||
    JSON.stringify(productSettings.vehicleTitleSettings) !==
      JSON.stringify(originalSettings.value.vehicleTitleSettings) ||
    productSettings.useCustomImageAbove !==
      originalSettings.value.useCustomImageAbove ||
    productSettings.imageUrl !== originalSettings.value.imageUrl
  )
})

const handleFontsLoaded = () => {
  console.log('Fonts loaded from hidden TypographySettings')
  // Now both components can use the same fonts
}

// Methods matching dashboard pattern
const handleSave = async () => {
  try {
    await productSettings.updateSettings()
    // Re-capture original settings after successful save
    captureOriginalSettings()
  } catch (error) {
    console.error('Failed to save settings:', error)
  }
}

const handleReset = () => {
  // Reset to original settings
  if (originalSettings.value) {
    productSettings.aboveTextSettings = {
      ...originalSettings.value.aboveTextSettings,
    }
    productSettings.belowTextSettings = {
      ...originalSettings.value.belowTextSettings,
    }
    productSettings.aboveImageSettings = {
      ...originalSettings.value.aboveImageSettings,
    }
    productSettings.belowImageSettings = {
      ...originalSettings.value.belowImageSettings,
    }
    productSettings.typographySettings = {
      ...originalSettings.value.typographySettings,
    }
    productSettings.showBrandImage = originalSettings.value.showBrandImage
    productSettings.brandImage = originalSettings.value.brandImage
    productSettings.vehicleTitleSettings = {
      ...originalSettings.value.vehicleTitleSettings,
    }
  }
}

// Dynamic icon based on alignment value
const getAlignmentIcon = (alignment: string) => {
  switch (alignment) {
    case 'left':
      return 'mdi-format-align-left'
    case 'center':
      return 'mdi-format-align-center'
    case 'right':
      return 'mdi-format-align-right'
    default:
      return 'mdi-format-align-left'
  }
}

// Upload custom image to S3 via upload-service
const onCustomImageSelected = async (files: any) => {
  const pick = (f: any) => {
    if (
      f &&
      typeof f === 'object' &&
      typeof f.size === 'number' &&
      typeof f.type === 'string'
    ) {
      productSettings.aboveImageSettings.file = f
      return f as File
    }
    return undefined
  }
  if (!files) return
  const file: File | undefined = Array.isArray(files)
    ? pick(files[0])
    : pick(files)
  if (!file) return

  try {
    const juid = accountSettings.accountId || 'unknown'
    const ext = inferExtension(file.type)
    // Versioned filename to avoid CDN cache; S3 will store a new object each time
    const key = `scan2sell/uploads/${juid}/customImage-${Date.now()}.${ext}`
    const { uploadUrl, publicUrl } = await getPresignedUploadUrl({
      key,
      contentType: file.type,
      bucket: getComponentsBucketName(),
    })
    await putFileToS3(uploadUrl, file, file.type)
    productSettings.imageUrl = publicUrl
  } catch (e) {
    console.error('Custom image upload failed:', e)
  }
}

// When Show image is turned off, clear custom image selections
const onToggleShowImage = (value: boolean) => {
  if (!value) {
    productSettings.useCustomImageAbove = false
    productSettings.aboveImageSettings.file = undefined
    // Keep imageUrl so it can be reused when Use Custom Image is toggled back on
  }
}

// When enabling Use Custom Image, if a saved URL exists, prefer it
const onToggleUseCustomImage = (value: boolean) => {
  if (value) {
    if (productSettings.imageUrl && productSettings.imageUrl.trim() !== '') {
      // Clear any previous session file so preview prints the saved URL
      productSettings.aboveImageSettings.file = undefined
    }
  }
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 16px;
}

.space-y-6 > * + * {
  margin-top: 24px;
}

.gap-4 {
  gap: 16px;
}

/* Consistent with dashboard styling */
:deep(.v-expansion-panels) {
  border-radius: 8px;
}

:deep(.v-expansion-panel-title) {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

:deep(.v-expansion-panel-text) {
  padding: 16px 0;
}
</style>
